'use client';

import { createContext, type ReactNode, useContext, useState } from 'react';

import type { SelectedGiftOption } from '../_components/GiftDonateButton';

interface SelectedOption {
  optionGroupId: number;
  optionItemId: number;
  extraProductPrice: number;
}

interface GiftOptionContextValue {
  selectedOptions: Map<number, SelectedOption>;
  handleOptionSelect: (
    optionGroupId: number,
    optionItemId: number,
    extraProductPrice: number,
  ) => void;
  handleOptionDelete: (optionGroupId: number) => void;
  quantity: number;
  handleQuantityChange: (quantity: number) => void;
  getSelectedOptions: (
    optionGroups: {
      optionGroupId?: number;
      optionGroupName?: string;
      optionItemInfoList?: {
        optionItemId?: number;
        itemName?: string;
        extraProductPrice?: number;
      }[];
    }[],
  ) => SelectedGiftOption[];
  resetSelection: () => void;
}

const GiftOptionContext = createContext<GiftOptionContextValue | null>(null);

export const GiftOptionProvider = ({ children }: { children: ReactNode }) => {
  const [selectedOptions, setSelectedOptions] = useState<
    Map<number, SelectedOption>
  >(new Map());
  const [quantity, setQuantity] = useState(1);

  // 옵션 선택 시 (그룹당 하나만 선택, 같은 옵션 클릭 시 선택 해제)
  const handleOptionSelect = (
    optionGroupId: number,
    optionItemId: number,
    extraProductPrice: number,
  ) => {
    setSelectedOptions((prev) => {
      const existing = prev.get(optionGroupId);
      const newMap = new Map(prev);

      if (existing?.optionItemId === optionItemId) {
        newMap.delete(optionGroupId);

        return newMap;
      }

      newMap.set(optionGroupId, {
        optionGroupId,
        optionItemId,
        extraProductPrice,
      });

      return newMap;
    });
    // 조합이 바뀌면 단가가 달라지므로 수량을 되돌린다
    setQuantity(1);
  };

  const handleOptionDelete = (optionGroupId: number) => {
    setSelectedOptions((prev) => {
      const newMap = new Map(prev);
      newMap.delete(optionGroupId);

      return newMap;
    });
    setQuantity(1);
  };

  const getSelectedOptions = (
    optionGroups: {
      optionGroupId?: number;
      optionGroupName?: string;
      optionItemInfoList?: {
        optionItemId?: number;
        itemName?: string;
        extraProductPrice?: number;
      }[];
    }[],
  ): SelectedGiftOption[] => {
    return Array.from(selectedOptions.values()).map((opt) => {
      const optionGroup = optionGroups.find(
        (g) => g.optionGroupId === opt.optionGroupId,
      );
      const optionItem = optionGroup?.optionItemInfoList?.find(
        (item) => item.optionItemId === opt.optionItemId,
      );

      return {
        optionGroupId: opt.optionGroupId,
        optionGroupName: optionGroup?.optionGroupName ?? '',
        optionItemId: opt.optionItemId,
        optionItemName: optionItem?.itemName ?? '',
        extraProductPrice: opt.extraProductPrice,
      };
    });
  };

  const resetSelection = () => {
    setSelectedOptions(new Map());
    setQuantity(1);
  };

  const value = {
    selectedOptions,
    handleOptionSelect,
    handleOptionDelete,
    quantity,
    handleQuantityChange: setQuantity,
    getSelectedOptions,
    resetSelection,
  };

  return (
    <GiftOptionContext.Provider value={value}>
      {children}
    </GiftOptionContext.Provider>
  );
};

export const useGiftOption = () => {
  const context = useContext(GiftOptionContext);
  if (!context) {
    throw new Error('useGiftOption must be used within GiftOptionProvider');
  }

  return context;
};
